import React, { useState, useRef, useEffect } from "react";
import { useApi, apiPut, apiDelete } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";
import { LoadingOverlay } from "./LoadingPanel.tsx";

interface Project {
  id: number;
  name: string;
}

interface DiscussionPoint {
  topic: string;
  context: string;
  suggestedQuestion?: string;
}

interface CallNotesResult {
  projectName: string;
  callNotes: string;
  discussionPoints: DiscussionPoint[];
  taskSummary: {
    total: number;
    overdue: number;
    dueSoon: number;
    blocked: number;
  };
}

interface FellowLink {
  id: number;
  project_id: number;
  channel_id: string | null;
  channel_url: string | null;
  label: string | null;
}

export function CallNotes() {
  const { data: projects } = useApi<Project[]>("/api/projects");
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<CallNotesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const notesRef = useRef<HTMLDivElement>(null);

  // Fellow link state
  const [fellowLink, setFellowLink] = useState<FellowLink | null>(null);
  const [fellowLinkLoading, setFellowLinkLoading] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  // Fetch Fellow link when project changes
  useEffect(() => {
    if (!selectedProject) {
      setFellowLink(null);
      return;
    }
    setFellowLinkLoading(true);
    setFellowLink(null);
    fetch(`/api/fellow-link/${selectedProject}`)
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch");
        return r.json();
      })
      .then((data) => {
        setFellowLink(data as FellowLink | null);
        setFellowLinkLoading(false);
      })
      .catch(() => {
        setFellowLink(null);
        setFellowLinkLoading(false);
      });
  }, [selectedProject]);

  const handleGenerate = async () => {
    if (!selectedProject) {
      setError("Please select a project");
      return;
    }

    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch("/api/call-notes/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: selectedProject }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to generate call notes");
      } else {
        setResult(data as CallNotesResult);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.callNotes);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      if (notesRef.current) {
        const range = document.createRange();
        range.selectNodeContents(notesRef.current);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };

  const handleSaveLink = async () => {
    if (!selectedProject || !linkUrl.trim()) return;
    setLinkError(null);

    try {
      const res = await fetch(`/api/fellow-link/${selectedProject}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channelUrl: linkUrl.trim(),
          label: linkLabel || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLinkError(data.error || "Failed to save link");
        return;
      }
      setFellowLink({
        id: 0,
        project_id: selectedProject,
        channel_id: data.channelId,
        channel_url: linkUrl.trim(),
        label: linkLabel || null,
      });
      setShowLinkForm(false);
      setLinkError(null);
    } catch (err) {
      setLinkError((err as Error).message);
    }
  };

  const handleRemoveLink = async () => {
    if (!selectedProject) return;
    try {
      await apiDelete(`/api/fellow-link/${selectedProject}`);
      setFellowLink(null);
      setLinkUrl("");
      setLinkLabel("");
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const openEditLink = () => {
    if (fellowLink) {
      setLinkUrl(fellowLink.channel_url || "");
      setLinkLabel(fellowLink.label || "");
    }
    setLinkError(null);
    setShowLinkForm(true);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Meeting Agenda Generator</h1>
        <p>Generate discussion points from project tasks for your weekly client call</p>
      </div>

      <div className="card">
        <div className="form-group">
          <label>Project</label>
          <SearchSelect
            options={projects?.map((p) => ({ value: p.id, label: p.name })) || []}
            value={selectedProject}
            onChange={(v) => {
              setSelectedProject(v);
              setResult(null);
              setError(null);
              setShowLinkForm(false);
            }}
            placeholder="-- Select Project --"
          />
        </div>

        {/* Fellow link status */}
        {selectedProject > 0 && !fellowLinkLoading && (
          <div className="fellow-link-section">
            {fellowLink ? (
              <div className="fellow-link-status">
                <span className="badge badge-success">Fellow Linked</span>
                <span style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
                  {fellowLink.label || "Library channel"} (channel {fellowLink.channel_id})
                </span>
                <button className="btn btn-sm btn-secondary" onClick={openEditLink}>
                  Edit
                </button>
                <button className="btn btn-sm btn-danger" onClick={handleRemoveLink}>
                  Unlink
                </button>
              </div>
            ) : (
              <div className="fellow-link-status">
                <span className="badge badge-neutral">Fellow Not Linked</span>
                <span style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
                  Link a Fellow library channel to check prior meetings and avoid duplicate topics
                </span>
                <button className="btn btn-sm btn-primary" onClick={() => { setShowLinkForm(true); setLinkError(null); }}>
                  Link Fellow Channel
                </button>
              </div>
            )}

            {showLinkForm && (
              <div className="fellow-link-form">
                <div className="form-group">
                  <label>Fellow Library Channel URL</label>
                  <input
                    type="text"
                    className="form-input"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://yourteam.fellow.app/library/c/..."
                  />
                  <div style={{ fontSize: "12px", color: "var(--color-text-muted)", marginTop: "4px" }}>
                    Open the project's library channel in Fellow and copy the URL from your browser.
                  </div>
                </div>
                <div className="form-group">
                  <label>Label (optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={linkLabel}
                    onChange={(e) => setLinkLabel(e.target.value)}
                    placeholder="e.g. Weekly Client Sync"
                  />
                </div>
                {linkError && (
                  <div style={{ color: "var(--color-danger)", fontSize: "13px", marginBottom: "8px" }}>
                    {linkError}
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px" }}>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveLink} disabled={!linkUrl.trim()}>
                    Save Link
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowLinkForm(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: "16px" }}>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={generating || !selectedProject}
          >
            {generating ? "Analyzing tasks & meetings..." : "Generate Call Notes"}
          </button>
        </div>

        {error && (
          <div style={{ color: "var(--color-danger)", marginTop: "12px" }}>
            {error}
          </div>
        )}
      </div>

      {generating && (
        <LoadingOverlay message="Analyzing tasks & meetings... This may take a moment." />
      )}

      {result && (
        <>
          {/* Summary stats */}
          <div className="card">
            <div className="card-header">
              <h2 style={{ margin: 0 }}>{result.projectName} — Task Summary</h2>
            </div>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "12px" }}>
              <div className="stat-box">
                <div className="stat-value">{result.taskSummary.total}</div>
                <div className="stat-label">Active Tasks</div>
              </div>
              <div className="stat-box">
                <div className="stat-value" style={{ color: result.taskSummary.overdue > 0 ? "var(--color-danger)" : undefined }}>
                  {result.taskSummary.overdue}
                </div>
                <div className="stat-label">Overdue</div>
              </div>
              <div className="stat-box">
                <div className="stat-value" style={{ color: result.taskSummary.dueSoon > 0 ? "var(--color-warning)" : undefined }}>
                  {result.taskSummary.dueSoon}
                </div>
                <div className="stat-label">Due This Week</div>
              </div>
              <div className="stat-box">
                <div className="stat-value" style={{ color: result.taskSummary.blocked > 0 ? "var(--color-danger)" : undefined }}>
                  {result.taskSummary.blocked}
                </div>
                <div className="stat-label">Blocked</div>
              </div>
            </div>
          </div>

          {/* Discussion points */}
          {result.discussionPoints.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 style={{ margin: 0 }}>Discussion Points</h2>
                <span className="badge badge-info">
                  {result.discussionPoints.length} item{result.discussionPoints.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div style={{ marginTop: "12px" }}>
                {result.discussionPoints.map((point, i) => (
                  <div key={i} className="discussion-point">
                    <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                      {i + 1}. {point.topic}
                    </div>
                    <div style={{ color: "var(--color-text-muted)", fontSize: "14px", marginBottom: "4px" }}>
                      {point.context}
                    </div>
                    {point.suggestedQuestion && (
                      <div style={{ fontSize: "13px", fontStyle: "italic", color: "var(--color-primary)" }}>
                        Ask: "{point.suggestedQuestion}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Copy-pasteable call notes */}
          <div className="card">
            <div className="card-header">
              <h2 style={{ margin: 0 }}>Call Notes for Fellow</h2>
              <button
                className={`btn btn-sm ${copied ? "btn-success" : "btn-primary"}`}
                onClick={handleCopy}
              >
                {copied ? "Copied!" : "Copy to Clipboard"}
              </button>
            </div>
            <div
              ref={notesRef}
              className="call-notes-output"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(result.callNotes) }}
            />
          </div>
        </>
      )}
    </div>
  );
}

/** Minimal markdown -> HTML for preview rendering */
function markdownToHtml(md: string): string {
  return md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    // Headers
    .replace(/^### (.+)$/gm, "<h4>$1</h4>")
    .replace(/^## (.+)$/gm, "<h3>$1</h3>")
    .replace(/^# (.+)$/gm, "<h2>$1</h2>")
    // Bold
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italic
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Unordered list items
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    // Wrap consecutive <li> in <ul>
    .replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>")
    // Horizontal rules
    .replace(/^---$/gm, "<hr>")
    // Line breaks for remaining lines
    .replace(/\n\n/g, "<br><br>")
    .replace(/\n/g, "<br>");
}
